/**
 * Navigation state model.
 *
 * Drives the app-level routing dispatcher in `src/app/routing/Router.tsx`.
 * All navigation-related state lives here — no React imports, no side effects.
 * The reducer + initial-state builder are in this file (Task 3); the Provider
 * wires them to React in `src/app/navigation/NavigationProvider.tsx`.
 *
 * Invariant: the `View` union below is the authoritative list of routable
 * screens. Adding a new screen requires (a) extending this union, (b) adding
 * a registry entry in one of `src/app/views/*.tsx`, (c) updating the dev-time
 * completeness list in `src/app/routing/view-registry.ts`.
 */

// View union. 8 orphans removed from the pre-Phase-06 union because no case
// in App.tsx's renderContent() matched them (the components were deleted or
// converted to drawers). Removing them here surfaces any stale callers via tsc.
// Orphans removed: tarefa-details, oportunidade-details, user-details,
// contas-receber-v2, contas-pagar-v2, suprimentos-produtos, permission-types,
// modulo-details.
export type View =
  | 'home'
  // Obras
  | 'obras'
  | 'obras-dashboard'
  | 'obras-jornadas'
  | 'obras-areas'
  | 'obras-ambientes'
  | 'obras-pontos'
  | 'obras-produtos'
  | 'obras-cabos'
  | 'obras-tubos'
  | 'obras-pessoas'
  | 'obras-servicos'
  | 'obras-vistorias'
  | 'obras-andamentos'
  | 'obras-cronograma'
  // OS
  | 'os-ativas'
  | 'os-agenda'
  // Comercial
  | 'oportunidades-ativas-v2'
  | 'oportunidades-encerradas-v2'
  | 'proposta-details'
  | 'proposta-parametro'
  | 'proposta-grupo-imagem-manager'
  | 'oportunidade-status'
  // Financeiro
  | 'bancos'
  | 'contas-bancarias'
  | 'centros-custo'
  | 'categorias-contabeis'
  | 'formas-pagamento'
  | 'contas-receber'
  | 'contas-pagar'
  | 'movimentos'
  | 'caixa'
  | 'comissao'
  | 'comissao-parametro'
  | 'comissao-bonus'
  | 'vendas'
  | 'proposta-resultado'
  | 'banco-details'
  | 'conta-details'
  | 'centro-custo-details'
  // Suprimentos
  | 'suprimentos-requisicoes-ativas'
  | 'suprimentos-requisicoes-encerradas'
  | 'suprimentos-movimentacao'
  | 'suprimentos-balanco'
  | 'suprimentos-cat'
  | 'suprimentos-triagem'
  | 'suprimentos-ordem-compra'
  | 'suprimentos-ordem-compra-status'
  | 'suprimentos-marca'
  | 'suprimentos-produto-categoria'
  | 'suprimentos-produto-subcategoria'
  | 'suprimentos-produto-tipo'
  | 'suprimentos-unidade-medida'
  | 'suprimentos-margem-tipo'
  | 'suprimentos-classificacao'
  // Config / cadastros
  | 'pessoas'
  | 'empreendimentos'
  | 'departamentos'
  | 'estados'
  | 'cidades'
  | 'fornecedores'
  | 'produtos'
  | 'tipos-ambiente'
  | 'tipos-ponto'
  | 'grupos-tipo-ponto'
  | 'tipos-servico'
  | 'tipos-vistoria'
  | 'profissional-tipo'
  | 'atraso-motivo'
  | 'file-categorias'
  | 'transition-rules'
  | 'obra-etapa'
  | 'obra-produto-status'
  | 'modulos'
  | 'departamento-details'
  | 'estado-details'
  | 'cidade-details'
  | 'tipo-ambiente-details'
  | 'tipo-ponto-details'
  | 'grupo-tipo-ponto-details'
  | 'api-keys'
  | 'api-keys-admin'
  | 'api-docs'
  | 'webhooks'
  // Admin
  | 'equipe'
  | 'perfis'
  | 'auditoria-logs'
  // Atividade
  | 'atividade-consultor'
  | 'atividade-config-areas'
  | 'atividade-config-tipos'
  // Projetos
  | 'projetos-ativos'
  | 'projetos-encerrados'
  | 'projeto-status'
  // PDF
  | 'pdf-template-manager'
  | 'pdf-layout-builder'
  // Tools / platform
  | 'painel-cargas'
  | 'orcamento-escopo'
  | 'config'
  | 'cable-routing'
  | 'google-chat-manager'
  | 'sync-os-fotos'
  | 'auto-services'
  | 'produtos-a-comprar'
  | 'notificacoes'
  // Misc
  | 'tarefas';

export type ComercialSubview = 'pipeline' | 'encerradas';

/**
 * All overlay drawer kinds across phases 1-11.
 *
 * Single source of truth: the `OVERLAY_DRAWER_KINDS` array drives both the
 * union type AND the URL parser allowlist (`url-bridge.ts`). Adding a new
 * kind here makes it pushable AND restorable from `?drawer=...` in one step,
 * without manual sync between the type and a parallel runtime set.
 *
 * Kinds whose renderer hasn't shipped yet are mapped to `null` in the host's
 * renderer registry — they typecheck, but resolving the entry at runtime logs
 * a dev warning and renders nothing.
 *
 * Convention reminder (Phase 1): viewing an existing record pushes a drawer
 * onto the URL stack; creating a new record stays in component-local state.
 *
 * Session drawers (e.g. `meu_perfil`) push with an empty `id` and serialize
 * as a single segment in the URL (`?drawer=meu_perfil`). Their entity is
 * implicit in the auth context, not addressable.
 */
export const OVERLAY_DRAWER_KINDS = [
  // Phase 1 (already implemented)
  'obra',
  'jornada',
  'obra_jornada',
  'projeto',
  'atividade',
  'oportunidade',
  'requisicao',
  'ordem_compra',
  // Phase 3 (foundational)
  'pessoa',
  // Phase 4 (OS)
  'os',
  'os_servico',
  'os_vistoria',
  'os_report_review',
  'os_requisicao',
  // Phase 5 (Suprimentos)
  'marca',
  'produto_categoria',
  'produto_subcategoria',
  'produto_tipo',
  'produto_unidade_medida',
  'produto_margem_tipo',
  'movimentacao_estoque',
  'orcamento',
  'cat',
  'ordem_compra_status',
  // Phase 6 (Comercial)
  'vendas',
  'proposta_resultado',
  'empreendimento',
  'oportunidade_status',
  'comissao_detalhe',
  // Phase 7 (Atividade) — `atividade` itself was added in Phase 1
  'atividade_area',
  'atividade_tipo',
  // Phase 8 (Obra dashboard)
  'obra_ambiente',
  'obra_andamento',
  'obra_area',
  'obra_envolvido',
  'obra_ponto',
  'obra_produto',
  'obra_servico',
  'obra_vistoria',
  'obra_tubo',
  'obra_cabo',
  'obra_etapa',
  'obra_arquivo',
  'obra_produto_status',
  // Phase 9 (Cadastros)
  'cidade',
  'fornecedor',
  'departamento',
  'estado',
  'ambiente_tipo',
  'ponto_tipo',
  'ponto_tipo_grupo',
  'servico_tipo',
  'vistoria_tipo',
  'profissional_tipo',
  'atraso_motivo',
  'file_categoria',
  'modulo',
  'tipos_cabo',
  'produto',
  // Phase 10 (Financeiro)
  'lancamento',
  'movimento',
  'forma_pagamento',
  // Phase 11 (Admin/Misc)
  'perfil',
  'usuario',
  'transition_rule',
  'log_permissao',
  'meu_perfil',
  'tarefa',
] as const;

export type OverlayDrawerKind = (typeof OVERLAY_DRAWER_KINDS)[number];

/**
 * Notification `entidade_tipo` values that are not drawer kinds themselves
 * but open an existing drawer for the same id. `os_revisao` (JOSH-81) is the
 * "OS ready for review" notification: on the web it opens the OS drawer, and
 * the manager reviews through the existing "Relatório" flow.
 */
const NOTIFICATION_DRAWER_ALIASES: Readonly<Record<string, OverlayDrawerKind>> = {
  os_revisao: 'os',
};

/**
 * Maps a notification's `entidade_tipo` to the overlay drawer kind to push.
 * Aliased types resolve to their drawer; every other value passes through
 * unchanged, as before.
 */
export function notificationDrawerKind(entidadeTipo: string): OverlayDrawerKind {
  return NOTIFICATION_DRAWER_ALIASES[entidadeTipo] ?? (entidadeTipo as OverlayDrawerKind);
}

/**
 * Session drawer kinds: their entity is implicit (current user, current
 * tenant) and the URL serializes them without an `:id` segment. Drawers in
 * this set ignore `recordId` from the renderer registry and derive their
 * data from `useAuth()` or equivalent session context.
 */
export const SESSION_DRAWER_KINDS: ReadonlySet<OverlayDrawerKind> = new Set([
  'meu_perfil',
]);

export type OverlayDrawerMode = 'view' | 'edit';

export interface OverlayDrawerState {
  kind: OverlayDrawerKind;
  id: string;
  mode: OverlayDrawerMode;
}

export interface EncerradasFilters {
  searchTerm: string;
  periodoSelecionado: string;
  mesAnoAtual: { mes: number; ano: number };
  periodoCustom: { inicio: string; fim: string };
  appliedFilters: {
    status: string[];
    tipos: string[];
    consultores: string[];
    origens: string[];
  };
}

export interface ViewParams {
  initialFilterValues?: Record<string, unknown>;
  periodFilter?: string;
  selectedObraId?: string;
  initialOpenRecordId?: string;
  returnState?: {
    drawerOpen?: boolean;
    selectedProjetoId?: string;
    selectedOportunidadeId?: string;
  };
}

export interface ObraReturnState {
  oportunidadeId: string;
  originView: 'oportunidades-ativas-v2' | 'oportunidades-encerradas-v2';
}

/**
 * Selection IDs. Kept flat so history snapshots are cheap to clone.
 * `unknown`-typed entries preserve the pre-Phase-06 `any` contracts of legacy
 * child components; tightening them is out of scope for this phase.
 */
export interface Selection {
  obraId: string | null;
  userId: string | null;
  bancoId: string | null;
  contaId: string | null;
  tipoId: string | null;
  tipoType: string | null;
  centroCustoId: string | null;
  pessoaId: string | null;
  obraEnvolvido: unknown;
  obraAndamento: unknown;
  obraAmbiente: unknown;
  obraArea: unknown;
  obraProjeto: unknown;
  obraEtapaId: string | null;
  empreendimentoId: string | null;
  departamentoId: string | null;
  estadoId: string | null;
  cidadeId: string | null;
  tipoAmbienteId: string | null;
  tipoPontoId: string | null;
  grupoTipoPontoId: string | null;
  tipoPermissaoId: string | null;
  propostaId: string | null;
  osServicoId: string | null;
  osServicoFotoId: string | null;
}

export const emptySelection: Selection = {
  obraId: null,
  userId: null,
  bancoId: null,
  contaId: null,
  tipoId: null,
  tipoType: null,
  centroCustoId: null,
  pessoaId: null,
  obraEnvolvido: null,
  obraAndamento: null,
  obraAmbiente: null,
  obraArea: null,
  obraProjeto: null,
  obraEtapaId: null,
  empreendimentoId: null,
  departamentoId: null,
  estadoId: null,
  cidadeId: null,
  tipoAmbienteId: null,
  tipoPontoId: null,
  grupoTipoPontoId: null,
  tipoPermissaoId: null,
  propostaId: null,
  osServicoId: null,
  osServicoFotoId: null,
};

// Build a fresh EncerradasFilters with current month/year. Called by
// buildInitialState and any consumer that needs a pristine filter object.
export function createEmptyEncerradasFilters(): EncerradasFilters {
  const now = new Date();
  return {
    searchTerm: '',
    periodoSelecionado: 'todo-periodo',
    mesAnoAtual: { mes: now.getMonth(), ano: now.getFullYear() },
    periodoCustom: { inicio: '', fim: '' },
    appliedFilters: { status: [], tipos: [], consultores: [], origens: [] },
  };
}

/**
 * History snapshot: the subset of state restored by GO_BACK.
 * Excludes overlay drawer (which dismisses rather than restores), transient
 * notification flags, and the history array itself.
 */
export interface NavigationSnapshot {
  view: View;
  subview: string | null;
  comercialSubview: ComercialSubview;
  encerradasFilters: EncerradasFilters;
  viewParams: ViewParams | null;
  selection: Selection;
  drawerOportunidadeId: string | null;
}

export interface NavigationState extends NavigationSnapshot {
  /**
   * Stack of open overlay drawers. Last element = top of stack (visually on top).
   * Empty array = no drawers open. Drawers stack vertically; closing the top
   * drawer pops the array and reveals whatever was beneath. The stack is
   * mirrored in the URL via `?drawer=kind:id[:mode],...` so F5 / copy-paste
   * link reproduces the full overlay state.
   */
  overlayDrawerStack: OverlayDrawerState[];
  obraReturnState: ObraReturnState | null;
  history: NavigationSnapshot[];
  requisicaoNotificacaoId: string | null;
  autoOpenSeparacao: boolean;
}

export type NavigationAction =
  | { type: 'NAVIGATE'; view: View; subview?: string | null; viewParams?: ViewParams | null }
  | {
      type: 'NAVIGATE_WITH_SELECTION';
      view: View;
      selection: Partial<Selection>;
      viewParams?: ViewParams | null;
    }
  | { type: 'GO_BACK' }
  | { type: 'RESET_TO_HOME' }
  | { type: 'FRESH_LOGIN_RESET' }
  | { type: 'SET_SUBVIEW'; subview: string | null }
  | { type: 'SET_COMERCIAL_SUBVIEW'; subview: ComercialSubview }
  | { type: 'SET_ENCERRADAS_FILTERS'; filters: EncerradasFilters }
  | { type: 'SET_VIEW_PARAMS'; params: ViewParams | null }
  | { type: 'PATCH_VIEW_PARAMS'; patch: Partial<ViewParams> }
  | { type: 'SET_SELECTION'; patch: Partial<Selection> }
  | { type: 'SET_SELECTED_OBRA_ID'; id: string | null }
  | { type: 'SET_DRAWER_OPORTUNIDADE_ID'; id: string | null }
  | { type: 'CONSUME_DRAWER_OPORTUNIDADE_ID' }
  | { type: 'SET_OBRA_RETURN_STATE'; state: ObraReturnState | null }
  | { type: 'PUSH_OVERLAY_DRAWER'; kind: OverlayDrawerKind; id: string; mode?: OverlayDrawerMode }
  | { type: 'POP_OVERLAY_DRAWER' }
  | { type: 'CLOSE_ALL_OVERLAY_DRAWERS' }
  | { type: 'REPLACE_TOP_OVERLAY_DRAWER'; kind: OverlayDrawerKind; id: string }
  | { type: 'SYNC_OVERLAY_DRAWER_STACK'; stack: OverlayDrawerState[] }
  | { type: 'SET_OVERLAY_DRAWER_MODE'; mode: OverlayDrawerMode }
  | {
      type: 'SET_REQUISICAO_NOTIFICATION';
      id: string | null;
      autoOpenSeparacao: boolean;
    };

// ---------------------------------------------------------------------------
// Initial state + reducer
// ---------------------------------------------------------------------------

import {
  isFreshLogin,
  readPersistedObraId,
  readPersistedView,
  VIEWS_REQUIRING_CONTEXT,
} from './storage';

export function buildInitialState(): NavigationState {
  const initialView = resolveInitialView();
  const initialObraId = isFreshLogin() ? null : readPersistedObraId();

  return {
    view: initialView,
    subview: null,
    comercialSubview: 'pipeline',
    encerradasFilters: createEmptyEncerradasFilters(),
    viewParams: null,
    selection: { ...emptySelection, obraId: initialObraId },
    drawerOportunidadeId: null,
    overlayDrawerStack: [],
    obraReturnState: null,
    history: [],
    requisicaoNotificacaoId: null,
    autoOpenSeparacao: false,
  };
}

function resolveInitialView(): View {
  if (isFreshLogin()) return 'home';
  const persisted = readPersistedView();
  if (!persisted) return 'home';
  if (VIEWS_REQUIRING_CONTEXT.includes(persisted)) return 'home';
  // storage returns string — narrow back to View at this trust boundary.
  return persisted as View;
}

function snapshot(state: NavigationState): NavigationSnapshot {
  return {
    view: state.view,
    subview: state.subview,
    comercialSubview: state.comercialSubview,
    encerradasFilters: state.encerradasFilters,
    viewParams: state.viewParams,
    selection: state.selection,
    drawerOportunidadeId: state.drawerOportunidadeId,
  };
}

function pushHistory(state: NavigationState): NavigationSnapshot[] {
  return [...state.history, snapshot(state)];
}

/**
 * Append `entry` to the overlay stack, ignoring a re-push of whatever is
 * already on top.
 *
 * Two identical adjacent entries render two identical drawers stacked on each
 * other — never a useful state, and the shape every double-dispatch bug takes:
 * a double click on a row, a StrictMode double-invoked mount effect, a
 * `startTransition` push racing an urgent update. Stacking the same kind+id
 * *non*-adjacently stays legal (oportunidade → atividade → same oportunidade),
 * so only the top is compared.
 */
function pushDrawerEntry(
  stack: OverlayDrawerState[],
  entry: OverlayDrawerState,
): OverlayDrawerState[] {
  const top = stack[stack.length - 1];
  if (top && top.kind === entry.kind && top.id === entry.id && top.mode === entry.mode) {
    return stack;
  }
  return [...stack, entry];
}

export function navigationReducer(
  state: NavigationState,
  action: NavigationAction,
): NavigationState {
  switch (action.type) {
    case 'NAVIGATE':
      return {
        ...state,
        history: pushHistory(state),
        view: action.view,
        subview: action.subview ?? null,
        viewParams: action.viewParams ?? null,
        selection: emptySelection,
      };

    case 'NAVIGATE_WITH_SELECTION':
      return {
        ...state,
        history: pushHistory(state),
        view: action.view,
        viewParams: action.viewParams ?? null,
        selection: { ...state.selection, ...action.selection },
      };

    case 'GO_BACK': {
      if (state.history.length === 0) {
        return {
          ...state,
          view: 'home',
          subview: null,
          selection: emptySelection,
          viewParams: null,
        };
      }
      const previous = state.history[state.history.length - 1];
      return {
        ...state,
        ...previous,
        history: state.history.slice(0, -1),
      };
    }

    case 'RESET_TO_HOME':
      return {
        ...state,
        view: 'home',
        subview: null,
        selection: emptySelection,
        viewParams: null,
        history: [],
      };

    case 'FRESH_LOGIN_RESET':
      // Full reset: wipe navigation, selection, history, and transient flags.
      // Persisted sessionStorage is left untouched — ProtectedRoute clears the
      // fresh-login flag after its loading animation, matching pre-Phase-06 behavior.
      return {
        ...buildInitialState(),
        view: 'home',
        selection: emptySelection,
      };

    case 'SET_SUBVIEW':
      return { ...state, subview: action.subview };

    case 'SET_COMERCIAL_SUBVIEW':
      return { ...state, comercialSubview: action.subview };

    case 'SET_ENCERRADAS_FILTERS':
      return { ...state, encerradasFilters: action.filters };

    case 'SET_VIEW_PARAMS':
      return { ...state, viewParams: action.params };

    case 'PATCH_VIEW_PARAMS':
      return {
        ...state,
        viewParams: state.viewParams
          ? { ...state.viewParams, ...action.patch }
          : { ...action.patch },
      };

    case 'SET_SELECTION':
      return { ...state, selection: { ...state.selection, ...action.patch } };

    case 'SET_SELECTED_OBRA_ID':
      return {
        ...state,
        selection: { ...state.selection, obraId: action.id },
      };

    case 'SET_DRAWER_OPORTUNIDADE_ID':
      return { ...state, drawerOportunidadeId: action.id };

    case 'CONSUME_DRAWER_OPORTUNIDADE_ID': {
      // Atomic read-modify-write of the proposta→oportunidade handoff slot:
      // push the drawer AND clear the id in a single action.
      //
      // Splitting it across two dispatches from an effect duplicated the
      // drawer, because both reads came from the same stale render closure —
      // StrictMode double-invokes mount effects, and `pushOverlayDrawer`
      // defers its dispatch through `startTransition` while the clear was
      // urgent, so the clear could not fence the push. Reading the id from
      // live `state` here makes any repeat invocation a no-op.
      const id = state.drawerOportunidadeId;
      if (!id) return state;
      return {
        ...state,
        drawerOportunidadeId: null,
        overlayDrawerStack: pushDrawerEntry(state.overlayDrawerStack, {
          kind: 'oportunidade',
          id,
          mode: 'view',
        }),
      };
    }

    case 'SET_OBRA_RETURN_STATE':
      return { ...state, obraReturnState: action.state };

    case 'PUSH_OVERLAY_DRAWER': {
      const mode: OverlayDrawerMode = action.mode ?? 'view';
      return {
        ...state,
        overlayDrawerStack: pushDrawerEntry(state.overlayDrawerStack, {
          kind: action.kind,
          id: action.id,
          mode,
        }),
      };
    }

    case 'POP_OVERLAY_DRAWER':
      return state.overlayDrawerStack.length === 0
        ? state
        : { ...state, overlayDrawerStack: state.overlayDrawerStack.slice(0, -1) };

    case 'CLOSE_ALL_OVERLAY_DRAWERS':
      return state.overlayDrawerStack.length === 0
        ? state
        : { ...state, overlayDrawerStack: [] };

    case 'REPLACE_TOP_OVERLAY_DRAWER': {
      // Swap the top of the stack in place. Used by sibling navigation inside
      // a drawer (e.g. prev/next between os_servico siblings) where the URL
      // should track the current sibling but a back press should close the
      // whole drawer rather than walk back through every sibling visited.
      // Pairs with the `replaceTopOverlayDrawer` action wrapper, which calls
      // `history.replaceState` so this state change does not produce a new
      // browser-history entry. Falls back to a push when the stack is empty.
      const stack = state.overlayDrawerStack;
      if (stack.length === 0) {
        return {
          ...state,
          overlayDrawerStack: [
            { kind: action.kind, id: action.id, mode: 'view' },
          ],
        };
      }
      const top = stack[stack.length - 1];
      return {
        ...state,
        overlayDrawerStack: [
          ...stack.slice(0, -1),
          { ...top, kind: action.kind, id: action.id },
        ],
      };
    }

    case 'SET_OVERLAY_DRAWER_MODE': {
      const stack = state.overlayDrawerStack;
      if (stack.length === 0) return state;
      const top = stack[stack.length - 1];
      return {
        ...state,
        overlayDrawerStack: [...stack.slice(0, -1), { ...top, mode: action.mode }],
      };
    }

    case 'SYNC_OVERLAY_DRAWER_STACK':
      return { ...state, overlayDrawerStack: action.stack };

    case 'SET_REQUISICAO_NOTIFICATION':
      return {
        ...state,
        requisicaoNotificacaoId: action.id,
        autoOpenSeparacao: action.autoOpenSeparacao,
      };

    default: {
      // Exhaustive check — TS errors here if a new action variant is added
      // without a matching case.
      const _exhaustive: never = action;
      void _exhaustive;
      return state;
    }
  }
}
